import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Toast } from '@humanforce/mango-ui-react';
import type { ToastRef } from '@humanforce/mango-ui-react';

interface ToastMessage {
  id: number;
  message: string;
  tone: 'positive' | 'critical' | 'caution' | 'neutral';
  // form-validation.md routes a submit-triggered server error to a critical
  // toast with `sticky: true`, so the user can't lose the message to the
  // auto-dismiss timer before they've read what to fix.
  sticky?: boolean;
}

export const useToast = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const nextId = useRef(0);

  const addToast = useCallback((message: string, tone: ToastMessage['tone'] = 'positive', options?: { sticky?: boolean }) => {
    const id = ++nextId.current;
    setToasts((current) => [...current, { id, message, tone, sticky: options?.sticky }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts((current) => current.filter((t) => t.id !== id));
  }, []);

  return useMemo(() => ({ addToast, removeToast, toasts }), [addToast, removeToast, toasts]);
};

interface ToastContainerProps {
  toasts: ToastMessage[];
  onClose: (id: number) => void;
}

export const ToastContainer = ({ onClose, toasts }: ToastContainerProps) => {
  const toastRef = useRef<ToastRef>(null);
  const shownIds = useRef(new Set<number>());
  const timeoutIds = useRef(new Map<number, number>());

  useEffect(() => () => {
    timeoutIds.current.forEach((id) => window.clearTimeout(id));
    timeoutIds.current.clear();
    shownIds.current.clear();
  }, []);

  useEffect(() => {
    const currentIds = new Set(toasts.map((t) => t.id));
    for (const [id, timeoutId] of timeoutIds.current) {
      if (currentIds.has(id)) continue;
      window.clearTimeout(timeoutId);
      timeoutIds.current.delete(id);
      shownIds.current.delete(id);
    }
    toasts.forEach((toast) => {
      if (shownIds.current.has(toast.id)) return;
      shownIds.current.add(toast.id);
      if (toast.sticky) {
        toastRef.current?.show({ tone: toast.tone, detail: toast.message, sticky: true });
        return;
      }
      toastRef.current?.show({ tone: toast.tone, detail: toast.message, life: 3000 });
      timeoutIds.current.set(toast.id, window.setTimeout(() => onClose(toast.id), 3000));
    });
  }, [onClose, toasts]);

  return (
    <>
      <Toast ref={toastRef} />
      <div aria-live="polite" className="sr-only">
        {toasts.map((toast) => <div key={toast.id} role="status">{toast.message}</div>)}
      </div>
    </>
  );
};
